import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ClipboardList, Plus, Search } from 'lucide-react';
import { api } from '../../api';
import { pode, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';
import { FormOs } from '../agenda/FormOs';
import {
  NOME_PRIORIDADE, NOME_RESULTADO, NOME_STATUS_OS, hora, type ConfigAgenda, type OsAgenda, type Tecnico,
} from '../agenda/comum';

type Item = OsAgenda & { fechada_em: string | null; resultado: string | null; criado_em: string };
const SITUACOES = [['abertas', 'Em aberto'], ['fechadas', 'Fechadas'], ['todas', 'Todas']] as const;

const quandoTexto = (o: Item) => {
  const dt = o.fechada_em ?? o.agendada_para;
  if (!dt) return { texto: 'A agendar', sub: `aberta ${new Date(o.criado_em).toLocaleDateString('pt-BR')}` };
  const d = new Date(dt);
  return { texto: d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) + ' ' + hora(dt), sub: o.fechada_em ? 'fechada' : 'agendada' };
};

export function ListaOs() {
  const { usuario } = useSessao();
  const navegar = useNavigate();
  const [params, setParams] = useSearchParams();
  const situacao = params.get('situacao') ?? 'abertas';
  const [busca, setBusca] = useState(params.get('busca') ?? '');
  const [itens, setItens] = useState<Item[] | null>(null);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [cfg, setCfg] = useState<ConfigAgenda | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [nova, setNova] = useState(false);
  const podeAbrir = pode(usuario, 'os.abrir');

  useEffect(() => {
    api<Tecnico[]>('/tecnicos').then(setTecnicos).catch(() => {});
    api<ConfigAgenda>('/agenda/config').then(setCfg).catch(() => {});
  }, []);
  useEffect(() => {
    const q = new URLSearchParams({ situacao });
    if (params.get('busca')) q.set('busca', params.get('busca')!);
    if (params.get('responsavel_id')) q.set('responsavel_id', params.get('responsavel_id')!);
    setErro(null);
    api<Item[]>(`/os?${q}`).then(setItens).catch((e) => setErro(e.message));
  }, [params, situacao]);
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get('busca') ?? '') === busca) return;
      const p = new URLSearchParams(params); busca ? p.set('busca', busca) : p.delete('busca'); setParams(p, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [busca]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtro = (k: string, v: string) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p); };

  return (
    <>
      <div className="cabecalho-pagina">
        <div>
          <span className="kicker">Operação</span>
          <h1 className="titulo">Ordens de serviço</h1>
        </div>
        {podeAbrir && <button className="btn" onClick={() => setNova(true)}><Plus size={18} aria-hidden /> Nova OS</button>}
      </div>

      <div className="barra-agenda">
        <div className="alternador" role="tablist">
          {SITUACOES.map(([v, r]) => (
            <button key={v} role="tab" aria-selected={situacao === v} className={situacao === v ? 'ativo' : ''} onClick={() => filtro('situacao', v === 'abertas' ? '' : v)}>{r}</button>
          ))}
        </div>
        <div className="filtros-os">
          <label className="campo-busca"><Search size={18} strokeWidth={1.7} aria-hidden />
            <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Número, cliente, parque ou o que foi pedido" aria-label="Buscar OS" />
          </label>
          <select className="filtro" value={params.get('responsavel_id') ?? ''} onChange={(e) => filtro('responsavel_id', e.target.value)} aria-label="Técnico">
            <option value="">Todos os técnicos</option>
            {tecnicos.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </select>
        </div>
      </div>

      {erro && <p className="alerta" role="alert">{erro}</p>}
      <section className="cartao tabela-cartao">
        {itens === null ? <p className="carregando-tabela">Carregando…</p> : itens.length === 0 ? (
          <div className="vazio"><ClipboardList size={28} aria-hidden /><p>Nenhuma OS {situacao === 'fechadas' ? 'fechada' : situacao === 'abertas' ? 'em aberto' : ''} encontrada.</p></div>
        ) : (
          <table className="tabela tabela-os">
            <thead><tr><th>OS</th><th>Situação</th><th>Cliente · parque</th><th>Tipo</th><th>Quando</th><th>Técnico</th></tr></thead>
            <tbody>
              {itens.map((o) => {
                const q = quandoTexto(o);
                return (
                  <tr key={o.id} onClick={() => navegar(`/os/${o.id}`)}>
                    <td><Link to={`/os/${o.id}`}><strong className="mono">{o.numero}</strong></Link>
                      <small><span className={`pill-prioridade p-${o.prioridade}`}>{NOME_PRIORIDADE[o.prioridade]}</span></small></td>
                    <td><span className={`status st-${o.status}`}>{NOME_STATUS_OS[o.status]}</span>
                      {o.resultado && <small>{NOME_RESULTADO[o.resultado]}</small>}</td>
                    <td>{o.cliente}<small>{o.local}</small></td>
                    <td>{o.tipo}<small className="descricao-curta">{o.descricao}</small></td>
                    <td className="mono">{q.texto}<small>{q.sub}</small></td>
                    <td>{o.responsavel ?? <span className="nota">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {itens && itens.length >= 500 && <p className="nota rodape-tabela">Mostrando as 500 primeiras. Use a busca para achar uma OS específica.</p>}
      </section>

      <Modal titulo="Nova OS" aberto={nova} aoFechar={() => setNova(false)}>
        {nova && <FormOs tecnicos={tecnicos} duracaoPadrao={cfg?.duracao_padrao_min ?? 60}
          aoSalvar={(r) => { setNova(false); navegar(`/os/${r.id}`); }} aoCancelar={() => setNova(false)} />}
      </Modal>
    </>
  );
}
